/**
 * Plan C 主应用程序入口
 * 
 * 核心职责：
 * 1. 初始化路由系统
 * 2. 检查用户会话
 * 3. 启动应用
 */

// Import Tailwind CSS (replaces CDN)
import './index.css'

import { router } from './router.js'
import { appState } from './state.js'
import { authAPI } from './api/index.js'

// 🚀 Lazy load components for better performance
// These will be loaded on-demand when needed
let loginModal, paymentModal, notificationCenter, installPrompt, languageSwitcher

// 初始化应用
async function initApp() {
  console.log('🚀 AAA Nightlife - Plan C Starting...')

  // 注册页面路由
  router.register('home', () => import('./pages/home.js'))
  router.register('register', () => import('./pages/register.js'))
  router.register('venues', () => import('./pages/venues.js'))
  router.register('venueDetail', () => import('./pages/venueDetail.js'))
  router.register('events', () => import('./pages/events.js'))
  router.register('eventDetail', () => import('./pages/eventDetail.js'))
  router.register('social', () => import('./pages/social.js'))
  router.register('profile', () => import('./pages/profile.js'))
  router.register('settings', () => import('./pages/settings.js'))
  router.register('my-tickets', () => import('./pages/my-tickets.js'))
  router.register('my-bookings', () => import('./pages/my-bookings.js'))
  router.register('membership', () => import('./pages/membership.js'))
  router.register('search', () => import('./pages/search.js'))
  router.register('favorites', () => import('./pages/favorites.js'))
  router.register('my-activities', () => import('./pages/my-activities.js'))
  router.register('my-coupons', () => import('./pages/my-coupons.js'))
  router.register('notification-settings', () => import('./pages/notification-settings.js'))
  router.register('edit-profile', () => import('./pages/edit-profile.js'))
  router.register('friends', () => import('./pages/friends.js'))
  router.register('messages', () => import('./pages/messages.js'))
  router.register('chat', () => import('./pages/chat.js'))
  router.register('reviews', () => import('./pages/reviews.js'))
  router.register('social-events', () => import('./pages/social-events.js'))

  // 添加认证守卫 - 只有Profile需要登录
  router.addGuard((pageName) => {
    const protectedPages = ['profile', 'my-tickets', 'my-bookings', 'membership']
    if (protectedPages.includes(pageName)) {
      if (!appState.getState().isAuthenticated) {
        appState.showNotification('Please login to access profile', 'warning')
        // 显示登录弹窗但允许访问其他页面
        loginModal.open('login')
        return false
      }
    }
    // Social页面允许未登录访问（只是部分功能受限）
    return true
  })

  // 检查用户会话
  try {
    const session = await authAPI.getSession()
    if (session && session.user) {
      appState.setUser(session.user)
    }
  } catch (error) {
    console.log('No active session')
  }

  // 初始化通知系统
  initNotificationSystem()

  // 初始化加载动画控制
  initLoadingSystem()

  // 绑定全局事件
  bindGlobalEvents()

  // 初始化路由
  router.init()
  
  // 🚀 懒加载非关键组件（不阻塞初始加载）
  initLazyComponents().catch(console.error)
  
  // 显示加载完成
  console.log('✅ App initialized successfully')
}

/**
 * 初始化通知系统 (懒加载)
 */
async function initNotificationSystem() {
  const { notificationCenter: nc } = await import('./components/NotificationCenter.js')
  notificationCenter = nc
  notificationCenter.init()
}

/**
 * 初始化其他组件 (懒加载)
 */
async function initLazyComponents() {
  // Load non-critical components in parallel
  const [loginModalModule, paymentModalModule, installPromptModule, languageSwitcherModule] = await Promise.all([
    import('./components/LoginModal.js'),
    import('./components/PaymentModal.js'),
    import('./components/InstallPrompt.js'),
    import('./components/LanguageSwitcher.js')
  ])
  
  loginModal = loginModalModule.loginModal
  paymentModal = paymentModalModule.paymentModal
  installPrompt = installPromptModule.installPrompt
  languageSwitcher = languageSwitcherModule.languageSwitcher
}

/**
 * 初始化加载动画控制
 */
function initLoadingSystem() {
  appState.subscribe((state) => {
    const loadingOverlay = document.getElementById('loading-overlay')
    if (loadingOverlay) {
      if (state.loading) {
        loadingOverlay.classList.add('show')
      } else {
        loadingOverlay.classList.remove('show')
      }
    }
  })
}

/**
 * 绑定全局事件
 */
function bindGlobalEvents() {
  // 底部导航点击
  document.querySelectorAll('.nav-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const page = btn.getAttribute('data-page')
      if (page) {
        router.navigate(page)
      }
    })
  })

  // 退出登录
  window.addEventListener('auth:logout', () => {
    appState.showNotification('Logged out successfully', 'success')
    router.navigate('home')
  })

  // 监听在线/离线状态
  window.addEventListener('online', () => {
    appState.showNotification('You are back online', 'success')
  })

  window.addEventListener('offline', () => {
    appState.showNotification('You are offline', 'warning')
  })
}

// 启动应用
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp)
} else {
  initApp()
}

// 导出全局对象（用于调试）
window.AAA = {
  router,
  appState,
  loginModal,
  version: '1.0.0'
}
